import React, { useState, useEffect, createContext, useContext } from 'react';
import axios from '../config/api';
import { API_URL } from '../config/api';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaUsername, setMfaUsername] = useState('');
  const [mfaPassword, setMfaPassword] = useState('');

  useEffect(() => {
    // Skip initial auth check on OAuth callback page — GoogleAuthCallback will handle it
    if (window.location.pathname === '/auth/google') {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`);
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const login = async (username, password) => {
    console.log('Login attempt:', { username, api_url: API_URL });
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { username, password });
      console.log('Login response:', response.data);
      
      // Check if MFA is required
      if (response.data.mfa_required) {
        setMfaRequired(true);
        setMfaUsername(username);
        setMfaPassword(password);
        return { mfaRequired: true };
      }
      
      localStorage.setItem('token', response.data.access_token);
      await checkAuth();
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      console.error('Error response:', error.response);
      throw error;
    }
  };

  const loginWithMfa = async (otpCode) => {
    console.log('MFA login attempt');
    try {
      const response = await axios.post(`${API_URL}/api/auth/login/mfa`, {
        username: mfaUsername,
        password: mfaPassword,
        otp_code: otpCode
      });
      
      localStorage.setItem('token', response.data.access_token);
      setMfaRequired(false);
      setMfaUsername('');
      setMfaPassword('');
      await checkAuth();
      return { success: true };
    } catch (error) {
      console.error('MFA login failed:', error);
      throw error;
    }
  };

  const loginWithRecoveryCode = async (recoveryCode) => {
    console.log('Recovery code login attempt');
    try {
      const response = await axios.post(`${API_URL}/api/auth/login/recovery`, {
        username: mfaUsername,
        password: mfaPassword,
        recovery_code: recoveryCode
      });
      
      localStorage.setItem('token', response.data.access_token);
      setMfaRequired(false);
      setMfaUsername('');
      setMfaPassword('');
      await checkAuth();
      return { 
        success: true, 
        remainingCodes: response.data.remainingCodes,
        message: response.data.message
      };
    } catch (error) {
      console.error('Recovery code login failed:', error);
      throw error;
    }
  };

  const cancelMfa = () => {
    setMfaRequired(false);
    setMfaUsername('');
    setMfaPassword('');
  };

  // Direct token setter for admin login flows that bypass normal auth
  const setToken = async (token) => {
    localStorage.setItem('token', token);
    await checkAuth();
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    localStorage.setItem('token', response.data.access_token);
    await checkAuth();
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setMfaRequired(false);
    setMfaUsername('');
    setMfaPassword('');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      loginWithMfa,
      loginWithRecoveryCode,
      cancelMfa,
      setToken,
      register, 
      logout, 
      loading,
      mfaRequired,
      mfaUsername,
      checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
}
